import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { OverlayProps } from '~/shared/components/Overlay';
import { usePortal } from '~/shared/hooks/usePortal/usePortal';
import { isSSR, useTranslation } from '~/shared/utils';
import { Modal } from '../../modal/modal.component';
import { usePageRequest } from '~/shared/utils/request';
import { DynamicBlockList } from '~/templates/blocks';
import { LineSpinner } from '../../Spinners/line-spinner/line-spinner.component';
import { Typography } from '../../typography/typography.component';

type Props = PropsWithChildren<{ url: string }> & Omit<OverlayProps, 'open' | 'onDismiss'>;

export const LinkPageOverlay = ({ url, children, headline }: Props) => {
    const [open, setOpen] = useState(false);
    const { data: pageData, isLoading, isFetching } = usePageRequest(url, open);

    const { translate } = useTranslation();

    // State to let the modal animation complete
    const [animationComplete, setAnimationComplete] = useState(false);

    // Use a portal to avoid html getting parsed by rich text parser
    const Portal = usePortal(isSSR ? null : document.querySelector('body'));
    const onDismiss = useCallback(() => setOpen(false), [setOpen]);

    // useEffect to let the modal animation complete (match modal css transition duration: 300ms)
    // otherwise it freezes mid-animation when opening
    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => setAnimationComplete(true), 300);
            return () => clearTimeout(timer);
        } else {
            setAnimationComplete(false);
        }
    }, [open]);

    if (url.startsWith('http')) {
        console.error(`Absolute url's are not supported!`);
        return <></>;
    }

    // Show content when open, animation is complete and page data is loaded
    const showContent = !!open && animationComplete && !isLoading && !isFetching;

    return (
        <>
            <button
                className="cleanButton"
                onClick={(event) => {
                    event.preventDefault();
                    setOpen(true);
                }}
            >
                {children}
            </button>
            <Portal>
                <Modal
                    key={open ? 'open' : 'closed'}
                    isOpen={open}
                    onClose={onDismiss}
                    headline={headline}
                    darkHeader
                    style={{ minHeight: '95vh' }}
                >
                    {!showContent ? (
                        <LineSpinner width={100} positionAbsolute />
                    ) : (
                        <>
                            {pageData ? (
                                <DynamicBlockList
                                    elements={
                                        pageData && 'pageElements' in pageData
                                            ? pageData.pageElements
                                            : []
                                    }
                                />
                            ) : (
                                <Typography
                                    variant="bodyLg"
                                    textAlign="center"
                                    as="p"
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                >
                                    {translate('message.contentUnavailable')}
                                </Typography>
                            )}
                        </>
                    )}
                </Modal>
            </Portal>
        </>
    );
};
